import { defineStore } from 'pinia';
import {
  LIVE_CHAT_UNAUTH_GROUP_ID,
  LIVE_CHAT_STAG_GROUP_ID,
  LIVE_CHAT_VIP_GROUP_ID,
  LIVE_CHAT_ONE_DEPOSIT_GROUP_ID,
  ONE_DEP_GROUPS_FOR_LIVE_CHAT,
  LIVE_CHAT_HAS_SEON_GROUP_ID,
  VIP_GROUPS_FOR_LIVE_CHAT,
} from '~/utils/constants';
import initLivechat from '~/utils/livechat.js';
import { assignDataToFields, createPopulateString } from '~/utils/strapiComponents/addStrapiData';
import { findIfPassesByGroups, findIfPassesByStag } from '~/utils/strapiComponents/filters';
import { useStoreMenuStatuses } from '../client/menuStatuses';
import { useStoreSsPlayer } from '../softswiss/player';
import { strapi } from '../transport/strapi';

export const useStoreChatOptions = defineStore('chatOptions', {
  state: () => ({
    showForAuthorized: true,
    showForGuests: false,
    stags: {
      includedStags: '',
      excludedStags: '',
    },
    groups: {
      includedGroups: '',
      excludedGroups: '',
    },
    isChatLoaded: false,
    requestPromise: null as Promise<any> | null,
  }),

  getters: {
    includedStags(): string[] | null {
      return this.stags.includedStags ? this.stags.includedStags.split(',') : null;
    },
    excludedStags(): string[] | null {
      return this.stags.excludedStags ? this.stags.excludedStags.split(',') : null;
    },
    includedGroups(): string[] | null {
      return this.groups.includedGroups ? this.groups.includedGroups.split(',') : null;
    },
    excludedGroups(): string[] | null {
      return this.groups.excludedGroups ? this.groups.excludedGroups.split(',') : null;
    },
    isChatAvailible(): boolean {
      if (!import.meta.browser) {
        return false;
      }

      if (!useStoreSsPlayer().isPlayerAuthorised && !this.showForGuests) {
        return false;
      }

      if (useStoreSsPlayer().isPlayerAuthorised) {
        if (!this.showForAuthorized) {
          return false;
        }

        const isAllowedByGroups = findIfPassesByGroups(
          this.groups.includedGroups,
          this.groups.excludedGroups
        );

        if (!isAllowedByGroups) {
          return false;
        }
      }

      const isAllowedByStag = findIfPassesByStag(
        this.stags.includedStags,
        this.stags.excludedStags
      );

      if (!isAllowedByStag) {
        return false;
      }

      return true;
    },
  },

  actions: {
    async loadChatOptions() {
      if (!this.requestPromise) {
        const populateString = createPopulateString(['stags', 'groups']);

        this.requestPromise = strapi()
          .get('chat-option', populateString)
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        assignDataToFields(this, response.data);

        return true;
      } else {
        return false;
      }
    },
    initChat(): Promise<boolean> {
      this.isChatLoaded = false;
      return new Promise((resolve) => {
        // @ts-ignore
        if (window.LiveChatWidget) {
          // @ts-ignore
          window.LiveChatWidget.call('destroy');

          // @ts-ignore
          delete window.LiveChatWidget;
        }
        const storeSsPlayer = useStoreSsPlayer();
        const storeMenuStatuses = useStoreMenuStatuses();

        const isPlayerInVipGroups = VIP_GROUPS_FOR_LIVE_CHAT.some((group) =>
          storeSsPlayer.playerGroupIds.includes(group)
        );
        const isPlayerHasSeonGroup = computed(() => {
          return storeSsPlayer.playerGroupIds.includes(BONUS_ABUSER_GROUP);
        });
        const isPlayerInOneDepGroups = ONE_DEP_GROUPS_FOR_LIVE_CHAT.some((group) =>
          storeSsPlayer.playerGroupIds.includes(group)
        );

        function initializeChat() {
          let groupToInitChatWith = LIVE_CHAT_STAG_GROUP_ID; // 1
          if (!storeSsPlayer.isPlayerAuthorised) {
            groupToInitChatWith = LIVE_CHAT_UNAUTH_GROUP_ID; // 16
          } else if (isPlayerHasSeonGroup.value) {
            groupToInitChatWith = LIVE_CHAT_HAS_SEON_GROUP_ID; // 28
          } else if (isPlayerInVipGroups) {
            groupToInitChatWith = LIVE_CHAT_VIP_GROUP_ID; // 13
          } else if (isPlayerInOneDepGroups) {
            groupToInitChatWith = LIVE_CHAT_ONE_DEPOSIT_GROUP_ID; // 22
          }
          initLivechat(groupToInitChatWith);
        }

        const visibilityLiveChatHandler = (data: { visibility: string }) => {
          if (window.innerWidth <= 960 && data.visibility === 'minimized') {
            // @ts-ignore
            LiveChatWidget?.call('hide');
            storeMenuStatuses.setSupportChatVisibility(false);
          }
        };

        initializeChat();

        // @ts-ignore
        window.LiveChatWidget.on('ready', () => {
          resolve(true);
          // @ts-ignore
          window.LiveChatWidget?.on('visibility_changed', visibilityLiveChatHandler);
          if (window.innerWidth <= 960) {
            // @ts-ignore
            window.LiveChatWidget?.call('hide');

            // @ts-ignore
            window.LiveChatWidget?.on('greeting_displayed', () => {
              storeMenuStatuses.setSupportChatVisibility(true);
              window.LiveChatWidget.call('maximize');
            });
          }

          this.isChatLoaded = true;
        });

        // @ts-ignore
        window.LiveChatWidget.init();

        if (storeSsPlayer.playerInfo.email) {
          // @ts-ignore
          window.LiveChatWidget.call('set_customer_email', storeSsPlayer.playerInfo.email);

          if (storeSsPlayer.playerInfo.first_name) {
            // @ts-ignore
            window.LiveChatWidget.call('set_customer_name', storeSsPlayer.playerInfo.first_name);
          }
        }
      });
    },
    setChatLoadingStatus(status: boolean) {
      this.isChatLoaded = status;
    },
  },
});
